import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   PhoneIcon,
   SearchMagnifyingGlassIcon,
} from '@forwardflow/icons'
import {
   Autocomplete,
   Button,
   ContentCard,
   Grid,
   Group,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchAvailableAreaCodes } from '@forwardflow/queries/onboarding.queries'
import { useJsApiLoader } from '@react-google-maps/api'
import { ENV } from '@forwardflow/helpers/configs'
import { DEV_DUMMY, isFreeFlow, useDevTools } from '../dev/devTools'

function useDebounce<T>(value: T, delay: number): T {
   const [debouncedValue, setDebouncedValue] = useState<T>(value)

   useEffect(() => {
      const handler = setTimeout(() => {
         setDebouncedValue(value)
      }, delay)

      return () => {
         clearTimeout(handler)
      }
   }, [value, delay])

   return debouncedValue
}

const libraries: ['places'] = ['places']
const MAX_AREA_CODES = 3

/** The three-digit US area code of a phone number, if it has one. */
const areaCodeOf = (phone?: string | null): string | null => {
   const digits = (phone || '').replace(/\D/g, '')
   const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
   return national.length === 10 ? national.slice(0, 3) : null
}

/**
 * Phone numbers: suggested from the team's Follow Up Boss phone numbers
 * (owner first, then invited agents), with city / area-code search kept as
 * the secondary path.
 */
export const PhoneNumberConfigurationV2 = () => {
   const {
      getNextStep,
      getPreviousStep,
      onboardingData,
      setCampaignConfiguration,
   } = useOnboardingStoreV2()

   const { isLoaded } = useJsApiLoader({
      id: 'google-map-script',
      googleMapsApiKey: ENV.GOOGLE_MAPS_API_KEY,
      libraries,
   })

   const selectedAreaCodes = useMemo(
      () => onboardingData?.campaignConfiguration?.area_codes || [],
      [onboardingData?.campaignConfiguration?.area_codes]
   )

   /* ---------- Suggestions from the team's phone numbers ---------- */

   const teamCodes = useMemo(() => {
      const users = [...(onboardingData.invitedUsers || [])].sort(
         (a, b) => Number(b.isOwner) - Number(a.isOwner)
      )
      const seen = new Set<string>()
      const out: { code: string; name: string; isOwner: boolean }[] = []
      users.forEach((u) => {
         const code = areaCodeOf(u.phone)
         if (!code || seen.has(code)) return
         seen.add(code)
         out.push({
            code,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
            isOwner: Boolean(u.isOwner),
         })
      })
      return isFreeFlow() && out.length === 0
         ? DEV_DUMMY.areaCodes.map((code, i) => ({ code, name: i === 0 ? 'you' : 'your team', isOwner: i === 0 }))
         : out.slice(0, MAX_AREA_CODES)
   }, [onboardingData.invitedUsers])

   // One availability check per suggested code (fixed slots, hooks stay stable).
   const check0 = useSearchAvailableAreaCodes(teamCodes[0]?.code || '')
   const check1 = useSearchAvailableAreaCodes(teamCodes[1]?.code || '')
   const check2 = useSearchAvailableAreaCodes(teamCodes[2]?.code || '')
   const checks = [check0, check1, check2]

   const suggestions = teamCodes.map((t, i) => {
      const res = checks[i]
      const list: number[] | undefined = res.data?.areaCodes
      const available = isFreeFlow()
         ? true
         : Array.isArray(list)
           ? list.map(String).includes(t.code)
           : undefined
      return { ...t, available, isChecking: res.isLoading }
   })
   const isSuggesting = suggestions.some((s) => s.isChecking)

   // Pre-select the available suggestions once, if nothing is chosen yet.
   const preselected = useRef(false)
   useEffect(() => {
      if (preselected.current || isSuggesting || selectedAreaCodes.length) return
      const ready = suggestions.filter((s) => s.available === true).map((s) => s.code)
      if (!ready.length) return
      preselected.current = true
      setCampaignConfiguration({ area_codes: ready.slice(0, MAX_AREA_CODES) })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isSuggesting, suggestions.map((s) => `${s.code}:${s.available}`).join(',')])

   /* ---------- Secondary: search by city or area code ---------- */

   const [showSearch, setShowSearch] = useState(false)
   const [citySearch, setCitySearch] = useState('')
   const [selectedCity, setSelectedCity] = useState('')
   const [areaCodeSearch, setAreaCodeSearch] = useState('')
   const [citySuggestions, setCitySuggestions] = useState<string[]>([])
   const debouncedCitySearch = useDebounce(citySearch, 300)

   useEffect(() => {
      if (!debouncedCitySearch || selectedCity) {
         if (!debouncedCitySearch) setCitySuggestions([])
         return
      }

      if (!isLoaded) return

      let cancelled = false

      const fetchSuggestions = async () => {
         try {
            const request = {
               input: debouncedCitySearch,
               includedPrimaryTypes: ['(cities)'],
               includedRegionCodes: ['us'],
            }
            const { suggestions } =
               await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                  request
               )
            if (cancelled) return
            setCitySuggestions(
               [...new Set(
                  suggestions
                     .map((s) => {
                        const full = s.placePrediction?.text?.toString() ?? ''
                        return full.split(',')[0].trim()
                     })
                     .filter(Boolean)
               )]
            )
         } catch {
            if (!cancelled) setCitySuggestions([])
         }
      }

      fetchSuggestions()
      return () => {
         cancelled = true
      }
   }, [debouncedCitySearch, selectedCity, isLoaded])

   const isSelectingRef = useRef(false)

   const handleCityChange = (value: string) => {
      setCitySearch(value)
      if (isSelectingRef.current) {
         isSelectingRef.current = false
         return
      }
      setSelectedCity('')
   }

   const handleCitySelect = (value: string) => {
      isSelectingRef.current = true
      setCitySearch(value)
      setSelectedCity(value)
      setCitySuggestions([])
   }

   const { data: areaCodesResponse, isLoading } =
      useSearchAvailableAreaCodes(selectedCity)

   const normalizedAreaCodeSearch = areaCodeSearch.replace(/\D/g, '').slice(0, 3)
   const { data: searchedAreaCodesResponse, isLoading: isAreaCodeLoading } =
      useSearchAvailableAreaCodes(
         normalizedAreaCodeSearch.length === 3 ? normalizedAreaCodeSearch : ''
      )

   const handleSelectAreaCode = (areaCode: string) => {
      if (selectedAreaCodes.length >= MAX_AREA_CODES) return
      if (selectedAreaCodes.includes(areaCode)) return
      setCampaignConfiguration({
         area_codes: [...selectedAreaCodes, areaCode],
      })
   }

   const handleRemoveAreaCode = (areaCode: string) => {
      setCampaignConfiguration({
         area_codes: selectedAreaCodes.filter((code) => code !== areaCode),
      })
   }

   /** Suggested codes that are available right now. */
   const suggestedReady = suggestions
      .filter((s) => s.available === true)
      .map((s) => s.code)
      .slice(0, MAX_AREA_CODES)

   /** Back from search: close it and put the suggested codes back. */
   const useSuggested = () => {
      setShowSearch(false)
      if (suggestedReady.length) {
         setCampaignConfiguration({ area_codes: suggestedReady })
      }
   }

   const isUsingSuggested =
      suggestedReady.length > 0 &&
      selectedAreaCodes.length === suggestedReady.length &&
      suggestedReady.every((c) => selectedAreaCodes.includes(c))

   const toggleAreaCode = (areaCode: string) =>
      selectedAreaCodes.includes(areaCode)
         ? handleRemoveAreaCode(areaCode)
         : handleSelectAreaCode(areaCode)

   // Onboarding is completed on the stage mapping step that follows.
   const handleNextStep = () => {
      getNextStep()
   }

   // Dev tools (hidden bar): three Colorado area codes.
   useDevTools({
      fill: () => setCampaignConfiguration({ area_codes: DEV_DUMMY.areaCodes }),
   })

   const codeButton = (areaCode: string, extra?: string) => {
      const isSelected = selectedAreaCodes.includes(areaCode)
      const full = !isSelected && selectedAreaCodes.length >= MAX_AREA_CODES
      return (
         <button
            key={areaCode}
            type="button"
            disabled={full}
            onClick={() => toggleAreaCode(areaCode)}
            className={`rounded-lg border px-4 py-2 text-sm transition-all ${
               isSelected
                  ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                  : full
                    ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                    : 'border-white/20 bg-white/5 text-white hover:border-[#9F7BFF]/50 hover:bg-[rgba(159,123,255,0.10)]'
            }`}
         >
            ({areaCode})
            {extra && <span className="text-white/50 ml-2 text-xs">{extra}</span>}
         </button>
      )
   }

   return (
      <Stack gap={32}>
         <ContentCard className="space-y-6">
            <Stack gap={4}>
               <p className="text-white font-semibold text-lg">Your phone numbers</p>
               <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
                  Forward Flow calls and texts from local numbers so leads pick
                  up. We picked area codes that match your team's phones.
               </p>
            </Stack>
            <Group className="rounded-[16px] border border-[#A684FF]/30 bg-[#A684FF]/5 px-6 py-3">
               <Group
                  justify="center"
                  align="center"
                  className="rounded-[8px] border-[0.667px] border-[#8E51FF]/30 w-10 h-10"
                  style={{
                     background:
                        'var(--fill-icon-linear, linear-gradient(135deg, rgba(79, 57, 246, 0.30) 0%, rgba(127, 34, 254, 0.30) 100%))',
                  }}
               >
                  <PhoneIcon color="#8E51FF" />
               </Group>
               <p className="text-white font-medium">
                  First 3 phone numbers are on us.
               </p>
            </Group>

            {/* Suggested */}
            <div className="space-y-3">
               <p className="text-white/90 text-sm font-medium">Suggested for you</p>
               {isSuggesting ? (
                  <div className="flex flex-wrap gap-2.5">
                     {[...Array(Math.max(1, teamCodes.length))].map((_, i) => (
                        <div key={i} className="rounded-lg bg-white/10 animate-pulse h-10 w-28" />
                     ))}
                  </div>
               ) : suggestions.length === 0 ? (
                  <p className="text-white/40 text-sm">
                     No phone numbers on your Follow Up Boss team yet. Search for
                     a city or area code below.
                  </p>
               ) : (
                  <div className="flex flex-wrap gap-2.5">
                     {suggestions.map((s) =>
                        s.available === false ? (
                           <span
                              key={s.code}
                              className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/40"
                              title="No numbers available in this area code right now"
                           >
                              ({s.code}) <span className="text-xs">unavailable</span>
                           </span>
                        ) : (
                           codeButton(s.code, s.isOwner ? 'your number' : s.name)
                        )
                     )}
                  </div>
               )}
               {!isUsingSuggested && suggestedReady.length > 0 && !isSuggesting && (
                  <button
                     type="button"
                     className="text-[#A684FF] text-sm font-medium hover:underline"
                     onClick={useSuggested}
                  >
                     Use the suggested area codes ({suggestedReady.join(', ')})
                  </button>
               )}
               {selectedAreaCodes.length > 0 && (
                  <p className="text-white/60 text-sm">
                     We'll get you {selectedAreaCodes.length === 1 ? 'a number' : `${selectedAreaCodes.length} numbers`} in{' '}
                     {selectedAreaCodes.map((c) => `(${c})`).join(', ')}.
                     {selectedAreaCodes.length >= MAX_AREA_CODES && (
                        <span className="text-[#A684FF] ml-2">Maximum 3 selected.</span>
                     )}
                  </p>
               )}
            </div>

            {/* Secondary: search */}
            {!showSearch ? (
               <button
                  type="button"
                  className="text-[#A684FF] text-sm font-medium hover:underline"
                  onClick={() => setShowSearch(true)}
               >
                  Want a different area code? Search by city or area code
               </button>
            ) : (
               <div className="space-y-5 border-t border-white/10 pt-5">
                  <div className="flex items-center justify-between">
                     <p className="text-white/90 text-sm font-medium">
                        Search by city or area code
                     </p>
                     <button
                        type="button"
                        className="text-[#A684FF] text-sm font-medium hover:underline"
                        onClick={useSuggested}
                     >
                        Back to suggested
                     </button>
                  </div>
                  <Autocomplete
                     label="Search by city name"
                     leftSection={<SearchMagnifyingGlassIcon width={16} />}
                     shouldLeftSectionBeFocused
                     placeholder="Search for a city to find available area codes..."
                     value={citySearch}
                     onChange={handleCityChange}
                     onOptionSubmit={handleCitySelect}
                     data={citySuggestions}
                     filter={({ options }) => options}
                  />

                  <TextInput
                     label="Or search directly by area code"
                     leftSection={<SearchMagnifyingGlassIcon width={16} />}
                     placeholder="Enter a 3-digit area code, e.g. 616"
                     value={areaCodeSearch}
                     onChange={(event) => {
                        setAreaCodeSearch(event.currentTarget.value.replace(/\D/g, '').slice(0, 3))
                     }}
                     inputMode="numeric"
                     maxLength={3}
                  />

                  {normalizedAreaCodeSearch.length === 3 && (
                     <div className="space-y-3">
                        <p className="text-white/60 text-sm">
                           {isAreaCodeLoading
                              ? 'Checking area code availability...'
                              : 'Area code results'}
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                           {!isAreaCodeLoading &&
                              searchedAreaCodesResponse?.areaCodes?.map((code: number) =>
                                 codeButton(code.toString())
                              )}
                        </div>
                        {!isAreaCodeLoading &&
                           searchedAreaCodesResponse?.areaCodes?.length === 0 && (
                              <p className="text-white/40 text-sm">
                                 No available phone numbers found for area code {normalizedAreaCodeSearch}.
                              </p>
                           )}
                     </div>
                  )}

                  <div>
                     {isLoading && selectedCity && (
                        <div className="flex flex-wrap gap-2.5">
                           {[...Array(4)].map((_, i) => (
                              <div
                                 key={i}
                                 className="rounded-lg bg-white/10 animate-pulse h-10 w-28"
                              />
                           ))}
                        </div>
                     )}

                     {!isLoading && selectedCity && areaCodesResponse?.areaCodes && (
                        <div className="space-y-3">
                           <p className="text-white/60 text-sm">Available area codes</p>
                           <div className="flex flex-wrap gap-2.5">
                              {areaCodesResponse.areaCodes.map((code: number) =>
                                 codeButton(code.toString())
                              )}
                           </div>
                        </div>
                     )}

                     {!isLoading &&
                        selectedCity &&
                        areaCodesResponse?.areaCodes?.length === 0 && (
                           <p className="text-white/40 text-sm">
                              No area codes found for "{selectedCity}"
                           </p>
                        )}
                  </div>
               </div>
            )}
         </ContentCard>
         <Grid>
            <Grid.Col span={3}>
               <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => getPreviousStep()}
               >
                  <ArrowLeftMdIcon width={12} /> Back
               </Button>
            </Grid.Col>
            <Grid.Col span={9}>
               <Button className="w-full" onClick={() => handleNextStep()}>
                  {selectedAreaCodes.length ? 'Save and Continue' : 'Skip for now'}{' '}
                  <ChevronRightIcon width={18} />
               </Button>
            </Grid.Col>
         </Grid>
      </Stack>
   )
}
